import {useMemo, useState} from 'react';

/**
 * This hook is used to paginate data.
 * @param {T[]} data - The data to be paginated.
 * @param {number} itemsPerPage - The number of items to be displayed per page.
 */
export const usePagination = <T,>(data: T[], itemsPerPage: number) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate the total number of pages based on the number of items per page
  const totalPages = useMemo(() => Math.ceil(data?.length / itemsPerPage), [data, itemsPerPage]);

  // Get the current data slice based on the current page
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data?.slice(startIndex, endIndex) ?? [];
  }, [currentPage, data, itemsPerPage]);

  const handlePrev = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  return {
    currentPage,
    totalPages,
    currentData,
    handlePrev,
    handleNext,
    goToPage,
  };
};
